import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Book() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatSteak} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">The Complete Guide</span>
            <h1>Fasting Carnivore</h1>
            <p>
              Stories of transformation. One powerful approach. Everything you need to begin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Book Details */}
      <section className="section book-hero-section">
        <div className="container">
          <div className="book-preview">
            <div className="book-cover-container">
              <img
                src={COVERS.carnivore}
                alt="Fasting Carnivore"
                className="book-cover-img"
              />
              <img
                src={COVERS.nanoFasting}
                alt="Nano Fasting"
                className="book-cover-img"
              />
            </div>
            <motion.div
              className="book-details"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>Transform Your Gut. Transform Your Mind.</h2>
              <p className="fs-lg">
                People who tried everything else. Then they tried this.
                These are their stories—what they did, what changed, and how you can follow the same path.
              </p>

              <div className="book-quote-block">
                <p className="book-quote-text">
                  "I saw myself in these stories. If they could do it, I knew I could too.
                  The community made all the difference."
                </p>
                <p className="book-quote-author">
                  — Sarah M.
                </p>
              </div>

              <ul className="book-feature-list">
                <li className="book-feature-item">✓ Real transformation stories you'll see yourself in</li>
                <li className="book-feature-item">✓ Exactly how to get started today</li>
                <li className="book-feature-item">✓ What to do when challenges come up</li>
                <li className="book-feature-item">✓ What to eat and how to prepare it</li>
                <li className="book-feature-item">✓ A community of thousands who get it</li>
              </ul>

              <div className="book-cta-wrapper">
                <Link to="/join" className="btn btn-primary btn-lg">
                  Join the Community
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What's Inside</span>
            <h2>Everything You Need to Transform</h2>
          </div>
          <div className="grid-3">
            <motion.div
              className="card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="card-content book-card-center">
                <h3 className="book-card-title">The Stories</h3>
                <p className="book-card-desc">
                  People who were where you are now. See how they transformed
                  and know that you can too.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="card-content book-card-center">
                <h3 className="book-card-title">The Path</h3>
                <p className="book-card-desc">
                  Know exactly what to do from day one. No confusion.
                  No guessing. Just results.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="card-content book-card-center">
                <h3 className="book-card-title">The Community</h3>
                <p className="book-card-desc">
                  You're not alone. Thousands are walking this path with you,
                  ready to support you every step.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Reader Reviews</span>
            <h2>What Readers Are Saying</h2>
          </div>
          <div className="grid-3">
            <TestimonialCard
              quote="I saw myself in these stories. Finally found an approach that made sense to me."
              author="Jennifer M."
            />
            <TestimonialCard
              quote="The simplicity is what got me. No counting, no tracking, just eat meat. I wish I'd found this sooner."
              author="Robert K."
            />
            <TestimonialCard
              quote="I gave this to my husband. We're both doing carnivore now. It's become our new normal."
              author="Sarah T."
            />
          </div>
          <div className="grid-3 book-testimonials-gap">
            <TestimonialCard
              quote="The community is what makes the difference. You're not doing this alone."
              author="Michael P."
            />
            <TestimonialCard
              quote="This isn't just another diet book. These are real people, real journeys, real transformation."
              author="Lisa R."
            />
            <TestimonialCard
              quote="I was skeptical at first. The stories convinced me to try it. So glad I did."
              author="Amanda C."
            />
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="section section-light">
        <div className="container">
          <div className="book-comparison-block">
            <h2 className="book-comparison-heading">This Isn't Another Diet Book</h2>
            <div className="grid-2 gap-xl">
              <div>
                <h4 className="book-comparison-subtitle">Most Diet Books:</h4>
                <ul className="book-comparison-list-light">
                  <li>Tell you what to eat</li>
                  <li>Give you rules to follow</li>
                  <li>Make you count calories</li>
                  <li>Rely on willpower</li>
                  <li>Feel like a lecture</li>
                </ul>
              </div>
              <div>
                <h4 className="book-comparison-accent">Fasting Carnivore:</h4>
                <ul className="book-comparison-list">
                  <li><strong>Shows</strong> you what's possible through story</li>
                  <li>Lets you discover your own path</li>
                  <li>Works with your body, not against it</li>
                  <li>Eliminates the need for willpower</li>
                  <li>Reads like a page-turner</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section">
        <div className="container">
          <div className="book-audience-block">
            <h2 className="book-audience-heading">Who This Book Is For</h2>
            <div className="grid-stack">
              <div className="book-audience-card">
                <p className="book-audience-text">
                  <strong>The Curious</strong> — You've heard about carnivore eating and want to learn more.
                  You're open to a different approach.
                </p>
              </div>
              <div className="book-audience-card">
                <p className="book-audience-text">
                  <strong>The Overwhelmed</strong> — You're tired of complicated diets and conflicting advice.
                  You want something simple that actually makes sense.
                </p>
              </div>
              <div className="book-audience-card">
                <p className="book-audience-text">
                  <strong>The Skeptic</strong> — You think this sounds different. Eating only meat?
                  But you're curious enough to hear the stories.
                </p>
              </div>
              <div className="book-audience-card">
                <p className="book-audience-text">
                  <strong>The Community Seeker</strong> — You don't want to do this alone. You want to connect
                  with others who are on the same journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Materials */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Included Free</span>
            <h2>Bonus Materials</h2>
          </div>
          <div className="grid-4 mx-auto-mid">
            <div className="book-bonus-item">
              <h4 className="book-bonus-title">Food List</h4>
              <p className="book-bonus-desc">What to eat & avoid</p>
            </div>
            <div className="book-bonus-item">
              <h4 className="book-bonus-title">30-Day Tracker</h4>
              <p className="book-bonus-desc">Daily journal & progress</p>
            </div>
            <div className="book-bonus-item">
              <h4 className="book-bonus-title">Quick Reference</h4>
              <p className="book-bonus-desc">One-page cheat sheet</p>
            </div>
            <div className="book-bonus-item">
              <h4 className="book-bonus-title">Troubleshooting</h4>
              <p className="book-bonus-desc">Solutions for issues</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta">
        <div className="container">
          <h2>Start Your Transformation Today</h2>
          <p>
            Join thousands who are transforming their health together.
          </p>
          <Link to="/join" className="btn btn-secondary btn-lg">
            Join the Community
          </Link>
          <p className="cta-footer-text">
            $97 one-time · Lifetime access
          </p>
        </div>
      </section>
    </>
  );
}

function TestimonialCard({ quote, author }: { quote: string; author: string }) {
  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="card-content book-testimonial-padding">
        <p className="book-testimonial-quote">"{quote}"</p>
        <p className="book-testimonial-author">— {author}</p>
      </div>
    </motion.div>
  );
}
